/**
 * Bildstile, eine einzige Quelle für Oberfläche und Bildgenerierung.
 *
 * Vorher stand jeder Stil doppelt: Beschriftung und Farben im Web, der
 * Prompt in der API. Ein Stil, den man nur an einer Stelle nachträgt, sähe
 * in der Oberfläche richtig aus und erzeugte trotzdem etwas anderes.
 *
 * Der `prompt` wird VORNE an den Bildprompt gehängt. Bildmodelle gewichten
 * frühe Wörter stärker, ein nachgestellter Stil geht bei langen Prompts
 * unter.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * WARUM NICHT DREISSIG BELIEBIGE, ABER ZWÖLF ANIME
 *
 * Die Liste war einmal ein Sortiment aus Designer-Spielereien: Cyanotypie,
 * Blaupause, Halbtondruck, Isometrie, Produktrender, flüssiges Chrom. Schön
 * anzusehen, aber niemand baut damit einen Feed, der auf TikTok
 * funktioniert -- und wer aus dreißig gleichwertigen Kacheln wählen soll,
 * wählt am Ende doch immer dieselben drei.
 *
 * Jeder Stil hier erfüllt drei Bedingungen: Er trägt eine ganze
 * Content-Säule (nicht nur ein Einzelbild), er verträgt grossen Text
 * darüber, und er sieht 2026 nicht nach KI aus.
 *
 * Anime ist die Ausnahme von der Sparsamkeit, und zwar aus einem Grund:
 * Für das Publikum sind Shinkai, Ghibli und Ufotable keine Nuancen
 * desselben Looks, sondern drei verschiedene Sprachen -- es erkennt sie
 * auf einem Zehntelbild und hat zu jeder eine Erwartung. Zwölf Anime-Stile
 * sind deshalb keine zwölf Varianten, sondern zwölf Entscheidungen. Sie
 * stehen in einer eigenen Gruppe, damit die Kachelreihe trotzdem
 * überschaubar bleibt.
 *
 * WAS BEWUSST NICHT DRIN IST, mit Grund:
 *   Verläufe, Glasmorphismus, Bento-Raster  -- Standardausgabe jedes
 *       KI-Werkzeugs, liest sich als absichtslos
 *   „Golden Hour", makellose Belichtung     -- 2026 eine KI-Signatur
 *   Y2K, VHS-Nostalgie als Selbstzweck      -- überdehnt, überall gleich
 *   Blaupause, Cyanotypie, Isometrie        -- Nische ohne Reichweite
 *
 * KÜNSTLERNAMEN, nur bei Anime und dort immer mit Beschreibung dahinter.
 * Hier stand einmal, Namen seien tabu. Das war für Fotografie richtig und
 * für Anime falsch: Eine Handschrift wie die von Shinkai lässt sich
 * benennen oder umständlich umschreiben, und die Umschreibung trifft sie
 * nur ungefähr. Weil Anbieter Namen filtern können, steht hinter jedem
 * trotzdem die volle Merkmalsliste -- fällt der Name weg, fällt der Stil
 * auf seine Beschreibung zurück statt in sich zusammen.
 *
 * VERBRANNTE PROMPT-WÖRTER, die hier nirgends vorkommen: `8K`,
 * `ultra-realistic`, `masterpiece`, `trending on artstation`,
 * `hyperdetailed`, `octane render` -- sie kippen moderne Modelle genau in
 * den künstlichen Look, den wir vermeiden wollen. Ebenso `Kodak Portra`
 * und `Cinestill 800T`: die meistgenutzten Film-Kürzel im Netz und
 * entsprechend abgegriffen.
 * ─────────────────────────────────────────────────────────────────────────
 */
export const STYLE_CATEGORIES = [
    { key: 'grafik', label: 'Grafik & Typografie', hint: 'große Schrift, Zahlen und Originalmaterial statt KI-Fotos' },
    { key: 'foto', label: 'Echt & Fotografie', hint: 'wirkt wie aufgenommen, nicht gerendert' },
    { key: 'anime', label: 'Anime & Manga', hint: 'die grossen Handschriften, jede erkennbar' },
    { key: 'gezeichnet', label: 'Gezeichnet & Gemalt', hint: 'Hand, Pinsel, Filzstift' },
    { key: 'gemacht', label: 'Druck & Handgemacht', hint: 'Papier, Farbe, Knete — greifbar' },
];
export const IMAGE_STYLES = [
    {
        key: 'graphic-editorial', label: 'Grafik-Karussell',
        hint: 'große Typografie, klare Farbflächen, Originalscreens; ohne Bildmodell',
        category: 'grafik', textFit: 3, colors: ['#8FE1AC', '#F2BD65'],
        prompt: 'editorial graphic poster, bold readable typography, flat coordinated colour fields, subtle concentric line pattern, generous spacing and a clear visual hierarchy',
    },
    /* ══════════════════════════════════════════════ Echt & Fotografie ══ */
    {
        key: 'photo-phone',
        label: 'Handyfoto, ganz normal',
        hint: 'wie von Freunden gepostet, bei Tageslicht, nicht als AI erkennbar',
        category: 'foto',
        textFit: 2,
        colors: ['#d9cbb4', '#5b6b5e'],
        /*
         * Der unauffälligste Look von allen: so, wie Leute wirklich posten.
         * Kein Blitz, kein Film, kein Kinolicht, sondern die normale
         * Handykamera mit ihren kleinen Fehlern. Genau die verraten echte Fotos
         * und fehlen AI-Bildern: leicht schiefer Ausschnitt, gemischtes Licht,
         * Hautporen, Alltagskram im Hintergrund, etwas Sensorrauschen.
         */
        prompt: 'casual smartphone photo posted by a friend, shot on a recent iPhone main camera, natural available light, true-to-life colours, slightly off-centre handheld framing, real skin texture with pores and small blemishes, natural unstyled hair, everyday clutter in the background, mild sensor noise in the shadows, no studio lighting, no retouching, unposed in-between moment',
    },
    {
        key: 'photo-flash',
        label: 'Blitzlicht-Schnappschuss',
        hint: 'wie mit dem Handy fotografiert, nachts, echt',
        category: 'foto',
        textFit: 1,
        colors: ['#1c2333', '#e8dcc8'],
        /*
         * Der wichtigste Stil im ganzen Sortiment. Über 400 Marken hinweg
         * schlägt echt wirkendes Bildmaterial poliertes um das Drei- bis
         * Fünffache bei Interaktion und Abschluss -- und der direkte
         * Kamerablitz ist dessen sichtbarster Ausdruck.
         *
         * Die Unregelmäßigkeiten stehen deshalb ausdrücklich im Prompt:
         * schiefer Horizont, Unordnung im Hintergrund, sichtbare Hautstruktur.
         * Ein Modell, dem man sie nicht ansagt, liefert Hochglanz.
         */
        prompt: 'candid amateur phone snapshot, harsh direct on-camera flash, bright hotspot on the subject, background falling into deep shadow, 26mm wide phone lens, f/1.8, off-center framing, slightly tilted horizon, mild motion blur, unretouched texture with nothing smoothed away, ordinary imperfect surroundings, unposed real moment',
    },
    {
        key: 'photo-film',
        label: 'Analogfilm bei Tag',
        hint: 'Korn, gedeckte Farben, Erinnerung',
        category: 'foto',
        textFit: 2,
        colors: ['#c4b9a4', '#6e6552'],
        /*
         * Beschreibt LICHTQUALITAET, nicht Tageszeit. Hier stand einmal
         * „flat overcast daylight" -- das schrieb jeder Szene bewoelkten Tag
         * vor, auch der am Lagerfeuer. Ein Stil bestimmt, WIE etwas aussieht,
         * nicht WAS zu sehen ist; alles andere kaempft gegen den Planer.
         */
        prompt: '35mm film photograph on Kodak Gold 200, soft natural light without harsh direct sun, warm golden Kodak colours with natural skin tones, fine organic grain clumping unevenly, gentle halation around highlights, slightly lifted blacks, 40mm lens at f/4, candid mid-action moment, imperfect analogue charm',
    },
    {
        key: 'photo-cinematic',
        label: 'Filmstill',
        hint: 'ein Licht, tiefe Schatten, Kinoformat',
        category: 'foto',
        textFit: 2,
        colors: ['#16323d', '#d4703c'],
        /*
         * Genau EINE Farbgradings-Angabe. Mehrere gemischt (teal-orange plus
         * warm vintage plus cool desaturated) ergeben Matsch -- das Modell
         * mittelt sie, statt sich zu entscheiden.
         */
        prompt: 'film still from a feature film, 85mm anamorphic lens at f/1.4, shallow depth of field with oval bokeh, a single motivated light source from one side, deep shadow falloff, faint atmospheric haze, teal shadows against sodium-orange highlights, desaturated midtones, fine grain, wide negative space in the upper third',
    },
    {
        key: 'photo-blur',
        label: 'Unscharf & körnig',
        hint: 'weich, verträumt, viel Platz für Text',
        category: 'foto',
        textFit: 3,
        colors: ['#5c4a63', '#d9b8c4'],
        /*
         * Der textfreundlichste Fotostil überhaupt und zugleich das Gegenteil
         * dessen, was ein Modell von allein tut: Unschärfe ABSICHTLICH, nicht
         * als Fehler. „on purpose" gehört wörtlich in den Prompt, sonst
         * schärft das Modell nach.
         */
        prompt: 'soft-focus photograph, heavy defocus blurred on purpose, layered film grain, diffused bloom around light sources, muted plum and dusty rose palette, subject reduced to a soft shape, large calm unbroken area in the upper two thirds, no sharp detail anywhere',
    },
    {
        key: 'photo-bw',
        label: 'Schwarzweiß-Reportage',
        hint: 'harter Kontrast, Korn, zeitlos',
        category: 'foto',
        textFit: 3,
        colors: ['#e8e8e8', '#141414'],
        /*
         * Die Dimension, die dem Sortiment ganz gefehlt hat: keine Farbe.
         * Farbe ist bei uns immer auch Konkurrenz zum Text -- ein
         * Schwarzweissbild traegt jede Akzentfarbe, ohne mit ihr zu streiten,
         * und wirkt nebenbei sofort ernster als alles andere hier.
         */
        prompt: 'black and white documentary photograph, high contrast with deep blacks and clean bright highlights, coarse silver halide grain, available light only, 35mm reportage framing, strong tonal separation, timeless monochrome, no colour anywhere',
    },
    {
        key: 'photo-neon',
        label: 'Neonnacht',
        hint: 'Leuchtreklame, nasser Asphalt, Magenta & Cyan',
        category: 'foto',
        textFit: 2,
        colors: ['#ff2fb9', '#00d5e5'],
        /*
         * Der Stadt-bei-Nacht-Look, an dem sich Kurzvideo seit Jahren
         * festhaelt -- und der einzige hier, bei dem die Farbe selbst der
         * Hauptdarsteller ist. Nasse Oberflaechen sind der Trick: Sie
         * verdoppeln jedes Licht und geben dem Bild Tiefe, die eine trockene
         * Strasse nie haette.
         */
        prompt: 'photograph lit by saturated coloured artificial light, intense magenta and cyan colour casts, nearby surfaces catching and mirroring the colour, deep black shadows between the pools of light, faint lens flare, handheld 35mm, electric after-dark mood',
    },
    {
        key: 'photo-macro',
        label: 'Nahaufnahme',
        hint: 'nah dran, alles dahinter cremig unscharf',
        category: 'foto',
        textFit: 2,
        colors: ['#7f8f7a', '#3a4438'],
        /*
         * Hiess einmal „Makro & Detail" und versprach Oberflaechen und
         * Mikrostruktur. Das Beispielbild hat gezeigt, was wirklich
         * herauskommt, sobald in der Szene ein Mensch vorkommt: eine
         * Nahaufnahme mit starker Freistellung. Das ist ein guter Stil -- nur
         * eben ein anderer als der versprochene, und ein Etikett, das nicht
         * haelt, ist schlimmer als ein bescheidenes.
         *
         * Unterschied zu „Unscharf & koernig": Dort ist ALLES weich, hier ist
         * das Motiv scharf und nur der Hintergrund zerfliesst.
         */
        prompt: 'close-up photograph taken with a long lens, razor-thin plane of focus on the subject, creamy bokeh filling the background, fine texture visible where the image is sharp, soft diffused light, strong subject separation',
    },
    {
        key: 'photo-studio',
        label: 'Studio-Licht',
        hint: 'weich ausgeleuchtet, ruhig, sauber',
        category: 'foto',
        textFit: 3,
        colors: ['#d8d2c8', '#8a8378'],
        /*
         * Hiess „Studio-Portraet" und verlangte einen nahtlosen
         * Hintergrundkarton -- und verstiess damit gegen die eigene Regel
         * dieser Datei: Ein Stil bestimmt, WIE etwas aussieht, nicht WO es
         * spielt. Das Beispielbild hat es vorgefuehrt: Die Szene sagte „Dach
         * bei Nacht", der Karton kam nie, und uebrig blieb ein Bild, das
         * seinen eigenen Namen nicht einloest.
         *
         * Der brauchbare Kern von „Studio" ist ohnehin nicht die Pappe,
         * sondern das LICHT: gross, weich, kontrolliert, das Motiv sauber
         * freigestellt. Das funktioniert auf einem Dach genauso wie in einem
         * Raum -- und bleibt der einzige bewusst polierte Stil hier, fuer die
         * Faelle, in denen jemand als Person fuer etwas einsteht.
         */
        prompt: 'softly lit photograph with studio-quality lighting, one large soft key light with gentle falloff, subtle rim light separating the subject from the background, controlled shadows without harshness, clean uncluttered composition, calm and deliberate framing, medium format clarity',
    },
    /* ═════════════════════════════════════════════════ Anime & Manga ══ */
    /*
     * Warum hier Namen stehen und sonst nirgends.
     *
     * Ein Fotostil laesst sich vollstaendig beschreiben -- Brennweite,
     * Blende, Lichtrichtung, Korn. Eine Anime-Handschrift nicht: Was Shinkai
     * von Ufotable trennt, sind hundert Entscheidungen ueber Licht, Linie
     * und Farbe, die zusammen einen Look ergeben, den jedes Bildmodell aus
     * dem Namen sofort trifft und aus zwanzig Adjektiven nur ungefaehr.
     *
     * Deshalb steht in jedem dieser Prompts der Name VORNE -- und dahinter
     * trotzdem die vollstaendige Beschreibung. Wenn ein Anbieter den Namen
     * filtert oder ignoriert, faellt der Stil nicht in sich zusammen,
     * sondern auf seine Merkmale zurueck. Das ist der Grund fuer die
     * scheinbare Doppelung: Sie ist die Absicherung.
     *
     * Alle zwoelf sind auseinandergezogen, keine zwei sind Varianten
     * voneinander: leuchtend, pastoral, zart, sauber, gluehend, kinetisch,
     * realistisch, dreckig, retro, verzerrt, verspielt, schwarzweiss.
     */
    {
        key: 'anime-shinkai',
        label: 'Anime · Shinkai',
        hint: 'leuchtende Himmel, Lens Flares, Sehnsucht',
        category: 'anime',
        textFit: 2,
        colors: ['#7fb3ff', '#ffb3d1'],
        /*
         * Der meistgewaehlte Anime-Look ueberhaupt, und fuer uns der
         * praktischste: Shinkai baut in fast jedem Bild einen weiten Himmel
         * ueber die Szene -- genau die ruhige Flaeche, auf die unser Text
         * kommt. Deshalb steht sie hier auch ausdruecklich im Prompt.
         */
        prompt: 'lush modern anime illustration in the style of Makoto Shinkai, hyper-detailed luminous light with volumetric god rays, warm saturated gradients bleeding into deep blue, prominent lens flare and light bloom, glittering specular highlights on every surface, meticulously painted backgrounds down to the smallest object, clean cel-shaded subjects with soft rim light, nostalgic yearning atmosphere, one calm uncluttered area for text',
    },
    {
        key: 'anime-ghibli',
        label: 'Anime · Ghibli',
        hint: 'Gouache, warmes Tageslicht, handgemalt',
        category: 'anime',
        textFit: 3,
        colors: ['#a8d5a2', '#e8d9a0'],
        /*
         * `no harsh outlines` muss mit: Ohne den Satz legen die Modelle den
         * dicken Cel-Strich darueber, den moderne Serien haben -- und der
         * ganze Grund fuer diesen Stil, die weiche gemalte Flaeche, ist weg.
         */
        prompt: 'hand-painted anime film background in the style of Studio Ghibli and Hayao Miyazaki, soft gouache and watercolour texture, gentle earthy palette of greens and warm ochres, rounded organic shapes, simple friendly designs with minimal linework, warm natural light, visible brushwork, hand-made warmth, no harsh outlines',
    },
    {
        key: 'anime-kyoani',
        label: 'Anime · Kyoto Animation',
        hint: 'zart, weiches Licht, viel Luft',
        category: 'anime',
        textFit: 3,
        colors: ['#ffd9e0', '#bcd6f2'],
        /*
         * Der textfreundlichste Anime-Stil: Yamada laesst um ihre Figuren
         * herum absichtlich Platz und schiebt den Vordergrund in die
         * Unschaerfe. Beides steht im Prompt, beides ergibt Textflaeche.
         */
        prompt: 'delicate modern anime illustration in the style of Kyoto Animation and Naoko Yamada, soft pastel palette, gentle diffused light with lens bloom and round bokeh, shallow depth of field with a blurred foreground, fine individual hair strands, subtle blush shading on skin, quiet intimate framing, tender melancholic mood, generous soft empty air around the subject',
    },
    {
        key: 'anime-cel',
        label: 'Anime · Cel-Shading',
        hint: 'sauber, kräftig, moderner Serien-Look',
        category: 'anime',
        textFit: 2,
        colors: ['#ff9ecd', '#6bb6ff'],
        /*
         * Der neutrale Anime-Stil ohne Handschrift: Wer „Anime" sagt und
         * keinen Namen im Kopf hat, meint das hier. Bewusst drin, damit die
         * elf anderen eine Wahl bleiben und keine Pflicht werden.
         */
        prompt: 'crisp modern anime cel-shaded illustration, clean confident linework of even weight, flat colour fills with hard-edged two-tone shadows, vivid saturated palette, bright even key light, dynamic perspective, contemporary television animation production quality, uncluttered background',
    },
    {
        key: 'anime-kon',
        label: 'Anime · Satoshi Kon',
        hint: 'realistisch, Alltag, leise unheimlich',
        category: 'anime',
        textFit: 2,
        colors: ['#8a94a6', '#c96a5e'],
        /*
         * Der einzige Anime-Stil mit erwachsenen Proportionen und gewoehnlichen
         * Gesichtern. Fuer Themen, bei denen grosse Augen und Glitzer die
         * Aussage kaputtmachen wuerden.
         */
        prompt: 'realistic anime illustration in the style of Satoshi Kon, naturalistic adult proportions and ordinary unidealised features, densely observed everyday detail, muted realistic palette, flat even light like a documentary photograph, precise thin linework, calm faintly unsettling atmosphere, cinematic framing with the subject small in a wide frame',
    },
    {
        key: 'anime-akira',
        label: 'Anime · Akira',
        hint: '80er-Zelluloid, Neon, nasse Nachtstadt',
        category: 'anime',
        textFit: 1,
        colors: ['#c0182b', '#0e2b3d'],
        prompt: 'late 1980s anime film cel in the style of Katsuhiro Otomo and Akira, hand-painted background dense with mechanical and architectural detail, deep reds against cold cyans, airbrushed light glows painted directly onto the cel, heavy black shadow shapes, gritty film grain, wide anamorphic framing',
    },
    {
        key: 'anime-90s',
        label: 'Anime · 90er',
        hint: 'Filmkorn, gedeckte Farben, VHS-Ära',
        category: 'anime',
        textFit: 2,
        colors: ['#c98b8b', '#6b7fa8'],
        /*
         * „subtitle bar area left empty" ist der Trick an diesem Stil: Der
         * 90er-Anime hatte unten immer einen Untertitelstreifen, das Modell
         * kennt das Muster -- und liefert damit gratis eine Textzone.
         */
        prompt: '1990s Japanese television animation cel in the style of Cowboy Bebop and Neon Genesis Evangelion, hand-painted gouache background, visible cel paint edges and slight registration wobble, limited animation palette of magenta and amber, chromatic bleed and scanline texture, analogue broadcast softness, thick uneven ink outlines, subtitle bar area left empty at the bottom',
    },
    {
        key: 'anime-shoujo',
        label: 'Anime · Shoujo',
        hint: 'Glitzer, Blüten, Pastell, grosse Augen',
        category: 'anime',
        textFit: 2,
        colors: ['#ffc2e2', '#c9b6ff'],
        prompt: '1990s shoujo manga illustration in the style of Naoko Takeuchi and CLAMP, large glittering eyes with multiple catchlights, flowing hair drawn strand by strand, floating flower petals and sparkle motifs, soft pastel pink and lilac palette, screentone gradients, decorative art nouveau framing, dreamy romantic atmosphere, delicate tapering linework',
    },
    /* ═══════════════════════════════════════════ Gezeichnet & Gemalt ══ */
    {
        key: 'illu-pastel',
        label: 'Pastell-Illustration',
        hint: 'matt, ruhig, gedeckte Töne',
        category: 'gezeichnet',
        textFit: 3,
        colors: ['#e8c9a8', '#a8b8c9'],
        /*
         * `matte finish` und `opaque` müssen mit: Ohne sie rendern die
         * Modelle Gouache als Aquarell, und der ruhige Flächencharakter --
         * der ganze Grund für diesen Stil -- ist weg.
         */
        prompt: 'soft anime-adjacent illustration, muted pastel palette, matte gouache texture, opaque flat colour blocks, minimal linework, soft directional light with gentle shadows, large calm areas of unbroken colour, gentle grain overlay, no fine detail in the upper half',
    },
    {
        key: 'illu-doodle',
        label: 'Filzstift-Kritzelei',
        hint: 'wackelig, handgemacht, weisses Papier',
        category: 'gezeichnet',
        textFit: 3,
        colors: ['#ffffff', '#ff5c8a'],
        /*
         * Die bewusst schlechte Zeichnung als Echtheitsbeweis: 2026 eine
         * Gegenbewegung zur KI-Glätte, und für Meme-artige Gemeinschaftsposts
         * unschlagbar. Die Fehler muss man ansagen -- ungleicher Strichdruck,
         * Lücken in der Füllung --, sonst zeichnet das Modell sauber.
         */
        /*
         * „flat scan filling the entire frame" musste rein: Mit blossem
         * „scanned paper texture" lieferte das Modell ein ABFOTOGRAFIERTES
         * Notizbuch -- Buchruecken, Schreibtisch, Schatten --, und die
         * eigentliche Zeichnung sass als kleine Insel in der Mitte. Bei einem
         * 9:16-Post ist damit die halbe Flaeche verschenkt.
         */
        prompt: 'naive marker doodle drawn directly on plain white paper, flat scan filling the entire frame edge to edge, no notebook and no desk visible, wobbly uneven lines, inconsistent stroke pressure, childlike proportions, three-colour felt-tip palette, uneven fill with visible gaps, subtle paper grain, lots of empty white space',
    },
    {
        key: 'illu-mystic',
        label: 'Mystisch & Okkult',
        hint: 'Pflaume, Gold, Mondphasen, Ornament',
        category: 'gezeichnet',
        textFit: 2,
        colors: ['#3b2340', '#c9a227'],
        /*
         * Für Wahrsagen, Tarot, Astrologie -- und damit für den Kunden, der
         * diese Anwendung überhaupt bezahlt. „Plum Noir" ist zugleich die
         * Trendfarbe 2026, das trifft sich gut.
         */
        prompt: 'dark occult editorial illustration, art nouveau ornamental border, deep plum and burnt burgundy with antique gold accents, esoteric symbolism rendered as fine line engraving, aged paper texture, matte print finish, symmetrical composition with an empty vignetted centre, candlelit chiaroscuro',
    },
    /* ══════════════════════════════════════════ Druck & Handgemacht ══ */
    {
        key: 'print-riso',
        label: 'Risograph',
        hint: 'zwei Farben, Fehldruck, grobes Raster',
        category: 'gemacht',
        textFit: 3,
        colors: ['#ff5c8a', '#2d9d8f'],
        prompt: 'risograph print, two-ink duotone in fluorescent pink and jade, visible misregistration offset between the colour layers, coarse halftone dot screen, uneven ink coverage with low-ink streaks, paper fibre texture, flat bold shapes, high contrast, generous empty paper area at the bottom, no gradients',
    },
    {
        key: 'print-silhouette',
        label: 'Silhouette',
        hint: 'eine Form, eine Farbe, viel Fläche',
        category: 'gemacht',
        textFit: 3,
        colors: ['#e2542c', '#141414'],
        /*
         * Der Spezialist für sehr grossen Text: emotional lesbar ohne
         * Detailrauschen. Wenn eine Zeile richtig gross werden soll, ist das
         * der Stil, bei dem sie auch lesbar bleibt.
         */
        /* „figures" statt „a single figure": Die Anzahl bestimmt die Szene,
           nicht der Stil -- sonst verliert ein Post mit zwei Menschen einen. */
        prompt: 'bold silhouette composition, the subject as flat black shapes against a solid persimmon background, hard-edged shapes only with no interior detail, long cast shadows, strong diagonal, minimalist poster, seventy percent of the frame is empty flat colour',
    },
    {
        key: 'print-collage',
        label: 'Collage & Scrapbook',
        hint: 'ausgerissen, geklebt, Klebeband',
        category: 'gemacht',
        textFit: 2,
        colors: ['#d9a05b', '#3d5a80'],
        prompt: 'analog mixed-media collage, torn magazine cutouts with rough paper edges, overlapping instant photo stickers with faded edges and light leaks, washi tape, ballpoint scribbles and marker annotations, a coffee stain, flatbed scan look, uneven drop shadows, kraft paper base, deliberately imperfect placement',
    },
    {
        key: 'render-clay',
        label: 'Knete & Stop-Motion',
        hint: 'Fingerabdrücke, kleine Bühne, sympathisch',
        category: 'gemacht',
        textFit: 2,
        colors: ['#ff9f68', '#5ec4b6'],
        /*
         * Der Ersatz für den ausgereizten Hochglanz-3D-Look. Entscheidend
         * sind die Spuren der Hand: Fingerabdrücke, unebene Oberflächen,
         * leicht schiefe Proportionen. Glatt gerendert wäre es genau das,
         * wovon sich der Feed absetzen soll.
         */
        prompt: 'hand-moulded plasticine stop-motion still, visible fingerprints and thumb marks in the clay, uneven lumpy surfaces, slightly off proportions, bright saturated colours, a tiny physical set with felt and cardboard props, soft practical studio lighting, shallow macro depth of field, real contact shadows, tabletop scale',
    },
];
export const STYLE_BY_KEY = Object.fromEntries(IMAGE_STYLES.map((s) => [s.key, s]));
export function isGraphicStyle(key) { return key === 'graphic-editorial'; }
export function graphicStyleGuidance(key) {
    return isGraphicStyle(key) ? 'GESTALTUNGS-VORRANG: Grafik-Karussell, keine Fotos, Menschen, erfundenen Geräte oder Videoclips. Alle Szenen sind image. Eine klare Aussage je Folie mit großer Typografie, bei belegten Zahlen die Zahl als eigene Textebene und ihre verständliche Einordnung daneben. Originalscreens oder Produktfotos werden als gestaltetes Belegfenster direkt in die Grafik eingebettet; kein Smartphone nötig. Wähle dafür showsApp und die passende referenceIndex. Die Aussage muss zur gewählten Vorlage passen. Keine unbelegten Zahlen; Rechenbeispiele nur mit bestätigten Eingaben und ausdrücklich genannten Annahmen. Der Bildprompt beschreibt die gewünschte Information statt einer Personenszene. LAYOUT JE FOLIE über style, damit das Karussell gestaltet statt wie eine Folienvorlage wirkt: Hook als headline (sender = kurzes Etikett wie Mythos, Test, 3 Fehler oder leer). Gegenüberstellung als EINE Ebene style compare: "Seite A | Seite B", sender "Etikett A | Etikett B" (z. B. Espresso | Filter, Vorher | Nachher). Irrtum gegen Wahrheit als EINE Ebene style myth: "Irrtum | Richtigstellung". Belegte Zahl als style stat: text nur die Zahl mit Einheit (z. B. 25 s), sender die Einordnung in einem Satz. Abfolge oder Aufzählung als steps, list oder checklist mit " / " getrennt, sender als Überschrift. Wörtliche Aussage als quote. Mindestens die Hälfte der Folien zwischen Hook und Schluss sind solche Karten, nie drei Folien mit demselben Layout hintereinander. Zwei Ebenen nur für Überschrift plus kurze Erklärung. Die letzte Folie bündelt den Nutzen und den nächsten Schritt. Diese Gestaltungsregel geht allgemeinen Foto-, Geräte- und Einstellungsregeln vor.' : '';
}
/**
 * Alte Stil-Schlüssel auf die heutigen abbilden.
 *
 * Zwei Generationen liegen hier begraben: die ersten Kurznamen („anime",
 * „lofi") und das dreissigteilige Sortiment davor. Ein Projekt, das einen
 * entfallenen Stil gespeichert hat, bekäme ohne diese Tabelle stillschweigend
 * gar keinen -- der Feed sähe ab dem nächsten Lauf anders aus, ohne dass
 * jemand etwas geändert hätte.
 *
 * Zugeordnet wird jeweils auf den nächstliegenden verbliebenen Stil, nicht
 * auf irgendeinen: Wer Aquarell gewählt hatte, bekommt Pastell, nicht
 * Blitzlicht.
 */
const LEGACY_KEYS = {
    /*
     * Ausgemustert, weil kein Vorschaubild dazu entstanden ist. Ein Stil,
     * den man in der Auswahl nicht SIEHT, wird nicht gewaehlt -- er macht
     * die Reihe nur laenger. Jeder geht auf den naechstliegenden, der
     * geblieben ist.
     */
    'anime-ufotable': 'anime-shinkai',
    'anime-trigger': 'anime-cel',
    'anime-yuasa': 'anime-cel',
    'manga-ink': 'print-silhouette',
    /* Erste Generation, Kurznamen */
    lofi: 'illu-pastel',
    clean: 'photo-film',
    photo: 'photo-film',
    typo: 'print-silhouette',
    bold: 'print-riso',
    cinematic: 'photo-cinematic',
    ugc: 'photo-flash',
    threed: 'render-clay',
    retro: 'anime-90s',
    anime: 'anime-cel',
    gradient: 'illu-pastel',
    editorial: 'photo-cinematic',
    dark: 'photo-cinematic',
    /* Fotografie */
    'photo-natural': 'photo-film',
    'photo-ugc': 'photo-flash',
    /* 'photo-macro' stand hier einmal als Umleitung auf photo-blur -- den
       Stil gibt es inzwischen wieder, und resolveStyleKey nimmt echte
       Schluessel ohnehin zuerst. Der Eintrag waere ab jetzt toter Code. */
    'photo-aerial': 'photo-cinematic',
    'photo-dark': 'photo-cinematic',
    'photo-bright': 'photo-film',
    'photo-golden': 'photo-film',
    /* Illustration & Anime */
    /* „anime-modern" war die namenlose Umschreibung des Shinkai-Looks. Sie
       ist wieder ein benannter Stil -- der Schlüssel zeigt dorthin zurück. */
    'anime-modern': 'anime-shinkai',
    'anime-dark': 'anime-ufotable',
    'illu-watercolour': 'illu-pastel',
    'illu-ink': 'print-silhouette',
    'illu-storybook': 'illu-doodle',
    'illu-lofi': 'illu-pastel',
    'illu-vector': 'print-silhouette',
    'illu-collage': 'print-collage',
    /* Grafik & Design */
    'grafik-typo': 'print-silhouette',
    'grafik-bold': 'print-riso',
    'grafik-editorial': 'photo-cinematic',
    'grafik-gradient': 'illu-pastel',
    'grafik-brutalist': 'print-riso',
    'grafik-blueprint': 'print-silhouette',
    /* 3D & Render */
    'render-product': 'photo-cinematic',
    'render-iso': 'render-clay',
    'render-liquid': 'illu-pastel',
    /* Analog & Retro */
    'analog-film35': 'photo-film',
    'analog-polaroid': 'photo-blur',
    'analog-vhs': 'anime-90s',
    'analog-risograph': 'print-riso',
    'analog-halftone': 'print-riso',
    'analog-cyanotype': 'print-silhouette',
};
/** Löst einen gespeicherten Schlüssel auf, alt wie neu. */
export function resolveStyleKey(key) {
    if (STYLE_BY_KEY[key])
        return key;
    return LEGACY_KEYS[key] ?? key;
}
/**
 * Der Stil-Baustein für einen Bildprompt.
 *
 * `auto` gibt bewusst nichts zurück: Dann sucht der Planer pro Post den Look,
 * der zum Thema passt, statt einen festen über alles zu legen. `custom`
 * nutzt den frei getippten Prompt des Projekts.
 */
export function stylePrompt(key, customPrompt = '') {
    if (key === 'custom')
        return customPrompt.trim();
    return STYLE_BY_KEY[resolveStyleKey(key)]?.prompt ?? '';
}
/**
 * Stand der Beispielbilder. Hochzählen, wenn welche ausgetauscht oder
 * ergänzt werden.
 *
 * Der Grund ist unangenehm konkret: Wer die Stilauswahl geöffnet hat,
 * BEVOR die Bilder hochgeladen waren, hat sich vom Browser für jede
 * Kachel eine 404 merken lassen -- und die hielt sich auch, als die
 * Bilder längst ausgeliefert wurden. In der Anwendung stand dann
 * „Beispiel folgt", während der Server das Bild klaglos herausgab. Die
 * Zahl im Pfad macht daraus eine neue Adresse, die niemand als fehlend
 * gespeichert hat.
 */
export const STYLE_PREVIEW_VERSION = 3;
/** Pfad zum Beispielbild, sobald eines hinterlegt ist. */
export function stylePreviewPath(key) {
    return `/styles/${key}.jpg?v=${STYLE_PREVIEW_VERSION}`;
}
export const TEXT_STYLES = [
    {
        key: 'auto',
        label: 'Gemischt',
        hint: 'die AI wählt je Post, empfohlen',
        prompt: '',
    },
    {
        key: 'aussage',
        label: 'Aussage',
        hint: 'ein Fakt, der überrascht',
        prompt: 'Formate IRRTUM oder LISTE: Der Hook ist eine überraschende, überprüfbare Aussage zum Thema, und jede weitere Folie liefert einen neuen Fakt dazu.',
    },
    {
        key: 'frage',
        label: 'Frage',
        hint: 'lädt zum Kommentieren ein',
        prompt: 'Formate THESE, TYPEN oder IRRTUM: Der Hook ist eine echte Frage, die man in einem Kommentar beantworten kann und will, keine rhetorische und keine Ja-Nein-Frage. Die Folien liefern Antworten, über die man streiten kann; die Caption greift die Frage noch einmal auf.',
    },
    {
        key: 'these',
        label: 'These',
        hint: 'eine Behauptung, die widerspricht',
        prompt: 'Formate THESE oder IRRTUM: Der Hook ist eine zugespitzte Behauptung, der ein echter Teil der Zielgruppe widerspricht. Jede weitere Folie bringt ein neues, stärkeres Argument. Nie beleidigend, nie unwahr.',
    },
    {
        key: 'zahl',
        label: 'Zahl',
        hint: '„3 Dinge, die …"',
        prompt: 'Formate LISTE, ANLEITUNG oder GUIDE: Der Hook beginnt mit einer konkreten Zahl und verspricht eine überschaubare Aufzählung. Genau so viele nummerierte Punkte folgen, der stärkste zuletzt.',
    },
    {
        key: 'geschichte',
        label: 'Mini-Geschichte',
        /*
         * „innerhalb eines Posts" muss hier stehen. Der Hinweis lautete „über
         * die Szenen hinweg erzählt" -- und wurde prompt als Serie ueber
         * mehrere Posts gelesen, was eine ganz andere Einstellung ist. Zwei
         * Dinge, die beide „Geschichte" heissen, muessen sich in ihrem
         * Halbsatz unterscheiden.
         */
        hint: 'EIN Post, seine Szenen nacheinander',
        prompt: 'Formate MINI-GESCHICHTE oder POV: Die Szenen erzählen eine kleine Geschichte mit Anfang, Wendung und einer Pointe, die ein Detail vom Anfang aufgreift. Der Hook ist ein Moment mitten in der Situation, in der Gegenwart erzählt, höchstens zehn Wörter.',
    },
];
export const TEXT_STYLE_BY_KEY = Object.fromEntries(TEXT_STYLES.map((t) => [t.key, t]));
/**
 * Übliche Hashtag-Mengen.
 *
 * Auf TikTok und Instagram Reels wirken drei bis sechs treffende Tags am
 * besten; ab etwa zehn liest es sich als Spam und bringt nichts mehr. Die
 * frühere Obergrenze von 15 war deshalb keine Freiheit, sondern eine Falle.
 */
export const HASHTAG_MAX = 10;
/**
 * Muss ein Beitrag als KI-erzeugt gekennzeichnet werden?
 *
 * TikTok und Meta verlangen die Kennzeichnung für realistisch wirkende
 * KI-Bilder und -Videos. Anime, Knete, Zeichnung erkennt jeder als
 * gestaltet; dort ist sie freiwillig. „auto" kennzeichnet deshalb Videos,
 * Fotostile und alles, dessen Stil wir nicht kennen. „off" ist die
 * bewusste Entscheidung des Kunden auf eigenes Risiko.
 */
export function needsAiLabel(mode, opts) {
    if (mode === 'off')
        return false;
    if (mode === 'always')
        return true;
    if (opts.hasVideo)
        return true;
    const style = IMAGE_STYLES.find((s) => s.key === opts.imageStyle);
    return !style || style.category === 'foto';
}
//# sourceMappingURL=styles.js.map